import { ChangeDetectorRef, Component, HostListener, Inject, Input, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { OrderDetailModel } from '../../../state/model/order-detail.model';
import { QualityDocumentService } from '../../../service/document-quality.service';
import { Router } from '@angular/router';
import { DROPDOWN_BUTTON_ITEM_TYPE, DropdownButtonItem } from '@cv-invision/cv.component-library.ux';
import { DOCUMENT_ATTACHMENT_CATEGORIES } from '../../../model/quality.constant';
import { lastValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { formatDate } from '@angular/common';
import { PdfAttachmentsStateModel } from '../../../pdf-review/state/model/pdf-attachments-state.model';
import { Store } from '@ngxs/store';
import { OverviewSubHeaderService } from '../service/overview-sub-header.service';
import {
	BroadcastService,
	ChangeEventStatusChannelAction,
	CLEAR_TOASTER_NOTIFICATION,
	EVENT_STATUS,
	SignalRState,
	ToasterType
} from '@cv-invision/core';
import { OtherFileDataModel } from './model/other-file-data.model';
import { DocumentDropdownItem, DocumentSubmenu } from './model/download-buttons.model';

@Component({
	selector: 'cv-download-buttons',
	templateUrl: `./download-buttons.component.html`,
	styleUrls: ['./download-buttons.component.scss'],
	host: {
		class: 'align-items-center position-relative',
		'[class.d-inline-flex]': 'isAvailable',
		'[class.d-none]': '!isAvailable'
	}
})
export class DownloadButtonsComponent implements OnInit, OnDestroy {
	@Input() orderInboxModel: OrderDetailModel;
	private atComponentDestroy = new Subject();
	pdfAttachments$: Observable<PdfAttachmentsStateModel>;
	channels$: Observable<any> = this.store.select(SignalRState.channelEvents);

	isAvailable: boolean;
	pdfVersionItems: DocumentDropdownItem[] = [];
	attachmentItems: DocumentDropdownItem[] = [];
	loading: boolean;
	src: any;
	qrrReportPath: string;
	menuOpen = false;
	openSubmenu: DocumentSubmenu | null = null;
	itemType = DROPDOWN_BUTTON_ITEM_TYPE;

	private currentVersionItem: DocumentDropdownItem;
	private historicalVersionItems: DocumentDropdownItem[] = [];
	private qrrAttachmentItems: DocumentDropdownItem[] = [];
	private availablePdfItems: DocumentDropdownItem[] = [];
	private otherAttachmentItems: DocumentDropdownItem[] = [];

	get hasDropdownItems(): boolean {
		return this.pdfVersionItems.length > 0 || this.attachmentItems.length > 0;
	}

	get pdfVersionCount(): number {
		return this.pdfVersionItems.filter((item) => this.isDocumentOption(item)).length;
	}

	get attachmentCount(): number {
		return this.attachmentItems.filter((item) => this.isDocumentOption(item)).length;
	}

	get documentCount(): number {
		return this.pdfVersionCount + this.attachmentCount;
	}

	get buttonLabel(): string {
		if (this.loading) {
			return 'Loading...';
		}
		return this.hasDropdownItems ? `Documents (${this.documentCount})` : 'Documents';
	}

	/**
	 * Check if the item is a document option
	 * @param item @see DropdownButtonItem
	 * @returns boolean
	 */
	private isDocumentOption(item: DropdownButtonItem): boolean {
		return item?.type === DROPDOWN_BUTTON_ITEM_TYPE.OPTION;
	}

	constructor(
		private qualityDocumentService: QualityDocumentService,
		private router: Router,
		private store: Store,
		private overviewSubheaderService: OverviewSubHeaderService,
		private broadcastService: BroadcastService,
		private cdr: ChangeDetectorRef,
		@Inject(LOCALE_ID) private locale: string
	) {
		this.pdfAttachments$ = this.store.select((s) => s.quality.pdfAttachments);
		this.isAvailable = true;
		this.loading = true;
	}

	ngOnInit(): void {
		this.broadcastService
			.onEvent('loadPDFFail')
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((result) => {
				const payload = result?.event ?? result;
				if (payload === 'Not Available') {
					this.isAvailable = false;
				}
				this.finishLoading();
			});

		this.broadcastService
			.onEvent('loadPDFSuccess')
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(() => this.applyCurrentPdf());

		this.checkForPDFFile();
		this.buildDocumentMenu();

		this.channels$.pipe(takeUntil(this.atComponentDestroy)).subscribe((channels: any) => {
			if (channels && channels[`orders:${this.orderInboxModel.id}`]) {
				this.onOrderEvent(channels[`orders:${this.orderInboxModel.id}`].events);
			}
		});
	}

	@HostListener('document:click')
	onDocumentClick(): void {
		this.closeMenu();
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next(true);
		this.atComponentDestroy.complete();
	}

	/**
	 * Build the nested PDF versions and attachments menus.
	 */
	private buildDocumentMenu(): void {
		this.currentVersionItem = {
			type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
			label: 'View current PDF version',
			icon: 'fa-file-arrow-down',
			isCurrent: true,
			data: { action: 'view-current' }
		};
		this.refreshMenus();
		this.loadAvailablePdfAttachments();

		this.pdfAttachments$
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((pdfAttachments: PdfAttachmentsStateModel) => {
				if (pdfAttachments && pdfAttachments.attachments && pdfAttachments.attachments.data) {
					this.historicalVersionItems = pdfAttachments.attachments.data.map((document) => ({
						type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
						label: formatDate(document.documentCreatedDate, 'MMMM dd, yyyy - hh:mm aa', this.locale),
						icon: 'fa-file-arrow-down',
						data: {
							action: 'download-path',
							...document
						}
					}));
					this.refreshMenus();
				}
			});
	}

	/**
	 * Compose the visible menu lists from the latest loaded sources.
	 */
	private refreshMenus(): void {
		this.pdfVersionItems = [this.currentVersionItem, ...this.historicalVersionItems].filter(Boolean);
		const hasQrr = this.qrrAttachmentItems.some((item) => item.data?.action === 'qrr');
		const availablePdfItems = hasQrr
			? this.availablePdfItems.filter((item) => !/qrr/i.test(item.label || ''))
			: this.availablePdfItems;
		this.attachmentItems = [...this.qrrAttachmentItems, ...availablePdfItems, ...this.otherAttachmentItems];

		// If no attachments, show a label indicating that there are no available attachments.
		if (this.attachmentItems.length === 0) {
			this.attachmentItems = [
				{
					type: DROPDOWN_BUTTON_ITEM_TYPE.LABEL,
					label: 'No Available attachments'
				}
			];
		}
	}

	/**
	 * Load Available PDF reports into the Attachments submenu.
	 */
	private loadAvailablePdfAttachments(): void {
		this.qualityDocumentService
			.getOrderAvailablePdf(this.orderInboxModel.id)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((data) => {
				if (data && data.length) {
					this.availablePdfItems = data.map((element) => ({
						type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
						label: element.label,
						icon: 'fa-file-arrow-down',
						data: {
							action: element.key === 'ENV' ? 'gen-env' : 'available-pdf',
							...element
						}
					}));
				} else {
					this.availablePdfItems = [];
				}
				this.refreshMenus();
			});
	}

	/**
	 * Check for the PDF file load.
	 */
	private checkForPDFFile(): void {
		const isCurrentOrder = this.qualityDocumentService.currentPDFFileId() === this.orderInboxModel?.id;
		const cachedPdf = this.qualityDocumentService.loadCurrentPDF();

		if (isCurrentOrder && cachedPdf) {
			this.applyCurrentPdf();
			return;
		}

		this.downloadCurrentPdf();
	}

	/**
	 * Apply the cached PDF blob and leave the loading state.
	 */
	private applyCurrentPdf(): void {
		this.src =
			this.orderInboxModel && this.qualityDocumentService.currentPDFFileId() === this.orderInboxModel.id
				? this.qualityDocumentService.loadCurrentPDF()
				: this.src;
		this.finishLoading();
	}

	/**
	 * Download the current order PDF when it is not already cached.
	 */
	private downloadCurrentPdf(): void {
		if (!this.orderInboxModel?.id) {
			this.finishLoading();
			return;
		}

		this.loading = true;
		this.qualityDocumentService
			.getOrderDocumentAttachment(this.orderInboxModel.id, DOCUMENT_ATTACHMENT_CATEGORIES.PDF)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(
				(result) => {
					if (this.src) {
						this.finishLoading();
						return;
					}

					if (!result.length) {
						this.isAvailable = false;
						this.finishLoading();
						return;
					}

					this.qualityDocumentService
						.downloadPDFFile(this.orderInboxModel.id, result[0].path)
						.pipe(takeUntil(this.atComponentDestroy))
						.subscribe(
							(pdfResponse) => {
								this.src = pdfResponse.blob;
								this.finishLoading();
							},
							() => this.finishLoading()
						);
				},
				() => this.finishLoading()
			);
	}

	private finishLoading(): void {
		this.loading = false;
		this.cdr.detectChanges();
	}

	/**
	 * Toggles the documents dropdown.
	 */
	toggleMenu(event: MouseEvent): void {
		event.stopPropagation();
		if (this.loading || !this.src) {
			return;
		}
		this.menuOpen = !this.menuOpen;
		this.openSubmenu = this.menuOpen ? 'versions' : null;
	}

	/**
	 * Opens a nested submenu without closing the parent menu.
	 */
	setOpenSubmenu(submenu: DocumentSubmenu): void {
		this.openSubmenu = submenu;
	}

	/**
	 * Closes the documents dropdown and any open submenu.
	 */
	private closeMenu(): void {
		this.menuOpen = false;
		this.openSubmenu = null;
	}

	onSelectItem(item: DocumentDropdownItem): void {
		this.closeMenu();
		this.onViewPDFversion(item);
	}

	/**
	 * Download File..
	 */
	onDownloadFile(src: any = null, filename?: string, fileExt = 'pdf'): void {
		const element = document.createElement('a');
		if (!src) {
			src = this.src;
			filename = this.orderInboxModel.description;
		}
		element.href = URL.createObjectURL(src);
		element.download = `${filename}.${fileExt}`;
		document.body.appendChild(element);
		element.click();

		this.broadcastService.broadcast({
			name: CLEAR_TOASTER_NOTIFICATION,
			event: {}
		});
	}

	/**
	 * View PDF version file.
	 */
	onViewPDFversion(item: DocumentDropdownItem): void {
		const action = item?.data?.action;
		let showToaster = true;
		console.log('item', item);
		if (action === 'view-current' || item.isCurrent) {
			this.router.navigate([`intelligence/quality/pdfreview/${this.orderInboxModel.id}`]);
			showToaster = false;
		} else if (action === 'qrr' || item.label === 'Open QRR report') {
			this.loading = true;
			this.qualityDocumentService.downloadFile(this.qrrReportPath).subscribe(
				(result) => {
					this.loading = false;
					if (result.blob) {
						this.onDownloadFile(result.blob, 'QRR-' + this.orderInboxModel.id);
					}
				},
				(error) => {
					this.loading = false;
				}
			);
		} else if (action === 'available-pdf' && item.data?.key) {
			const reportKey = item.data.key;
			this.loading = true;
			this.qualityDocumentService.downloadNonChachedPDFFile(this.orderInboxModel.id, reportKey).subscribe(
				(data) => {
					this.loading = false;
					if (data && 'blob' in data) {
						this.onDownloadFile(data.blob, `${this.orderInboxModel.id}_${reportKey}`);
					}
				},
				() => {
					this.loading = false;
				}
			);
		} else if (item.data && item.data.path) {
			this.loading = true;
			this.qualityDocumentService.downloadFile(item.data.path).subscribe(
				(result) => {
					this.loading = false;
					if (result.blob) {
						this.onDownloadFile(result.blob, item?.label);
					}
				},
				(error) => {
					this.loading = false;
				}
			);
		} else if (action === 'gen-env' && item.data?.key === 'ENV') {
			this.otherFileTypes(item);
		}

		if (showToaster) {
			this.broadcastService.broadcast({
				name: ToasterType.SUCCESS,
				event: {
					message: 'Please be patient while we bundle up and download a fair amount of data for you.'
				}
			});
		}
	}

	/**
	 * function to download/generate other file types
	 * @param item @see DropdownButtonItem
	 */
	otherFileTypes(item: DropdownButtonItem): void {
		let serv: any;

		switch (item.data.key) {
			case 'ENV':
				this.loading = true;
				serv = this.qualityDocumentService.generateENVFile(
					this.orderInboxModel.id,
					this.orderInboxModel.documentId
				);
				break;
		}

		serv.subscribe(
			() => {
				this.loading = true;
			},
			() => {
				this.loading = false;
			}
		);
	}

	/**
	 * Process the response from the server or signalR for other file types
	 *
	 * @param fileData @see OtherFileDataModel
	 * @param isSignalREvent
	 */
	processOtherFilesStatus(fileData: OtherFileDataModel, isSignalREvent = false): void {
		switch (fileData.status) {
			case 'Processing':
				// do nothing, for now
				break;
			case 'Failed':
				this.loading = false;
				this.broadcastService.broadcast({
					name: ToasterType.ERROR,
					event: {
						error: fileData.errorMessage
					}
				});
				break;
			case 'Completed':
				this.handleCompletedEvent(fileData, isSignalREvent);
				break;
		}
	}

	/**
	 * Process COMPLETED event, to retrive the signed file from API
	 *
	 * @param fileData @see OtherFileDataModel
	 * @param isSignalREvent
	 */
	private async handleCompletedEvent(fileData: OtherFileDataModel, isSignalREvent = false): Promise<void> {
		let fileLink = fileData.link;
		if (isSignalREvent) {
			const fileStatus = await lastValueFrom(
				this.qualityDocumentService.getENVfileStatus(this.orderInboxModel.id, this.orderInboxModel.documentId)
			);
			// need to be removed after be fix their piece
			if ('env' in fileStatus) {
				fileLink = fileStatus?.env?.link;
			} else {
				fileLink = fileStatus?.link;
			}
		}

		this.qualityDocumentService.downloadFile(fileLink).subscribe(
			(result) => {
				this.loading = false;
				if (result.blob) {
					this.onDownloadFile(result.blob, 'ENV-' + this.orderInboxModel.id, 'env');
				}
			},
			(error) => {
				this.loading = false;
			},
			() => {
				this.loading = false;
			}
		);
	}

	/**
	 * Process signalR events, specific for the async file generation
	 * @param events
	 */
	onOrderEvent(events: any[]): void {
		events.forEach((channelEvent: any) => {
			let clearEvent = false;
			if (channelEvent.status === 'new') {
				switch (channelEvent.event) {
					case 'env-export-status':
						this.processOtherFilesStatus(channelEvent.data, true);
						break;
				}

				clearEvent = true;
				// Change event to old
				if (clearEvent) {
					const orderChannel = `orders:${this.orderInboxModel.id}`;
					this.store.dispatch(new ChangeEventStatusChannelAction(orderChannel, EVENT_STATUS.OLD));
				}
			}
		});
	}
}
